import React, { useState, useEffect } from 'react';
import { Layout, ConfigProvider, theme } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import Sidebar from './Sidebar';
import Logo from '@/components/common/Logo';
import { selectCurrentSpace } from '@/store/selectors/spaceSelectors';
import { useIntelligentPreloader } from '@/hooks/useIntelligentPreloader';
import { SpaceSyncGuard } from '@/hooks/useSpaceSyncGuard';
import GuideOverlay from '@/components/common/GuideOverlay';
import { useGuideOverlay } from '@/components/common/GuideOverlay/useGuideOverlay';
import { GuideStep } from '@/components/common/GuideOverlay/types';
import ToolsImage from '@/assets/guide/tools-movie.gif';
import HomeText from '@/assets/guide/home-text.gif';
import HomeTextEn from '@/assets/guide/home-text-en.gif';
import { useLanguage } from '@/contexts/LanguageContext';
import i18n from '@/i18n/config';
const { Content, Sider } = Layout;

// 定义路由状态类型
interface RouteState {
  showSidebar?: boolean;
}

const MainLayout: React.FC = () => {
  const borderColor = 'rgba(255,255,255,0.06)';
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [guideSteps, setGuideSteps] = useState<GuideStep[]>([]);
  const location = useLocation();
  const state = location.state as RouteState | null;
  const { isEnglish, currentLanguage, t } = useLanguage();
  // 团队空间：切换后整树 remount，各页面 useEffect 首拉天然按新空间（新 token）执行
  const currentSpace = useSelector(selectCurrentSpace);
  const spaceKey = currentSpace?.teamId ?? 'personal';

  // 引导功能
  const {
    visible: guideVisible,
    currentStep,
    start: startGuide,
    stop: stopGuide,
    next: nextGuide,
    prev: prevGuide,
  } = useGuideOverlay({
    steps: guideSteps,
    // onComplete: () => {
    //   // 引导完后缓存添加
    //   localStorage.setItem('guide_overlay_shown', 'true');
    // },
  });

  const closeGuideHandler = () => {
    stopGuide();
    console.log('引导完成！');
    // 引导完后并根据当前currentStep的key缓存到localstorage中
    const guideShown = JSON.parse(localStorage.getItem('guide_shown') || '{}');
    const currentStepKey = guideSteps[currentStep]?.key;
    if (currentStepKey) {
      guideShown[currentStepKey] = true;
      localStorage.setItem('guide_shown', JSON.stringify(guideShown));
    }
    console.log('guideShown', guideShown);
  };

  // 判断在home页，并且缓存中没有引导过，则启用引导
  useEffect(() => {
    //  localstorage 中 guide_shown 的格式为 { home: 'true', tools: 'true' }
    const guideShown = JSON.parse(localStorage.getItem('guide_shown') || '{}');
    if (location.pathname === '/creation/tools' && !guideShown.toolsYunJing) {
      setGuideSteps([
        {
          title: t('guide.cameraTemplateTitle'),
          content: '',
          key: 'toolsYunJing',
          image: ToolsImage,
          imageAlt: t('guide.cameraTemplateTitle'),
          top: 108,
          left: 278,
          indicator: {
            show: true,
            position: 'left',
            text: t('guide.cameraTemplateText'),
            style: {
              fontSize: '12px',
            },
          },
        },
      ]);
      startGuide();
    }
    if ((location.pathname === '/creation/image' || location.pathname === '/creation/video') && !guideShown.textImageSupper) {
      setGuideSteps([
        {
          title: t('guide.textWithImageTitle'),
          content: '',
          key: 'textImageSupper',
          image: isEnglish ? HomeTextEn : HomeText,
          imageAlt: t('guide.textWithImageTitle'),
          top: 278,
          left: 150,
          indicator: {
            show: true,
            position: 'left',
            text: t('guide.textWithImageText'),
            style: {
              fontSize: '12px',
            },
          },
        },
      ]);
      startGuide();
    }
  }, [location.pathname, isEnglish]);

  // 暂时禁用智能预加载，避免 Minified React error #306
  // useIntelligentPreloader(); // 启用智能预加载功能

  // 判断当前是否为图片创作、视频创作或小工具页面
  const isCreationPage =
    location.pathname.includes('/creation/image') ||
    location.pathname.includes('/creation/video') ||
    location.pathname.includes('/creation/tools') ||
    location.pathname.includes('/creation/edit-video-detail') ||
    location.pathname.includes('/editor');

  // 判断当前是否为资产页面，且状态中明确指定不显示侧边栏
  const isAssetsPageWithoutSidebar =
    location.pathname.includes('/assets') && state?.showSidebar === false;



  // 当路由变化时，根据页面类型和路由状态控制侧边栏显示
  useEffect(() => {
    if (isCreationPage || isAssetsPageWithoutSidebar) {
      // 对于图片、视频创作或小工具页面，或者标记为不显示侧边栏的资产页面，隐藏侧边栏
      setIsDrawerOpen(false);
    } else {
      // 对于其他页面，显示侧边栏
      setIsDrawerOpen(true);
    }
  }, [location.pathname, state, isCreationPage, isAssetsPageWithoutSidebar]);

  return (
    <ConfigProvider
      locale={isEnglish ? enUS : zhCN}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorBgBase: '#121212',
          colorBgContainer: '#121212',
          borderRadius: 4,
          colorPrimary: '#FF6A39',
          colorLink: '#FF6A39',
        },
      }}
    >
      <Layout style={{ height: 'calc(var(--vh, 1vh) * 100)', background: '#121212' }}>
        <Sider
          width={232}
          theme="dark"
          style={{
            background: '#121212',
            position: 'fixed', // 使用固定定位，不受右侧内容影响
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 200,
            // overflow: 'hidden',
            borderRight: 'none',
            display: isCreationPage || isAssetsPageWithoutSidebar ? 'none' : 'block',
          }}
          collapsed={!isDrawerOpen}
          collapsedWidth={0}
          trigger={null}
        >
          {/* 左侧logo区域 */}
          <Logo />
          {/* 右侧边框线，确保完整覆盖整个侧边栏高度 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              width: '1px',
              background: borderColor,
              zIndex: 4,
            }}
          ></div>

          <Sidebar />
        </Sider>
        <Layout
          style={{
            background: '#121212',
            marginLeft: isCreationPage || isAssetsPageWithoutSidebar ? 0 : 232, // 为固定的 Sidebar 留出空间
          }}
        >
          <Content
            style={{
              padding: '0',
              overflow: 'auto',
              /* 隐藏滚动条但保留滚动功能 */
              scrollbarWidth: 'none' /* Firefox */,
              msOverflowStyle: 'none' /* IE and Edge */,
            }}
            className="main-content-scrollable"
          >
            <div
              key={spaceKey}
              style={{
                background: '#121212',
                minHeight: 'calc(var(--vh, 1vh) * 100)',
                boxShadow: 'none',
              }}
            >
              {/* 空间切换跨标签提示：覆盖左侧菜单全部路由（home/创作/历史/米粒值等） */}
              <SpaceSyncGuard>
                <Outlet /> {/* 子路由内容将在这里渲染 */}
              </SpaceSyncGuard>
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* 引导组件 */}
      <GuideOverlay
        visible={guideVisible}
        steps={guideSteps}
        currentStep={currentStep}
        onClose={closeGuideHandler}
        onNext={nextGuide}
        onPrev={prevGuide}
        onComplete={closeGuideHandler}
        maskClosable={true}
        showCloseButton={true}
        showNavigation={true}
        showStepIndicator={true}
      />
    </ConfigProvider>
  );
};

export default MainLayout;
